/* eslint-disable*/
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_URL } from "../config.js";
import "./order.css";
import AsyncSelect from "react-select/async";
import { useRef } from "react";
import {
  faChevronLeft,
  faChevronRight,
  faDownload,
  faEnvelope,
  faMobileAlt,
  faPencil,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import profileImg from "../assets/img/profile.png";
import blueTick from "../assets/img/bluetick.png";
import LoadingSpinner from "../components/LoadingSpinner";
import { showToast } from "../components/toast";
import { PermissionWrapper, hasPermission } from "../helpers/permission.js";
import AssignParentOrderPopup from "./assignParentOrderPopup.jsx";

const isFieldEditable = (id, fieldName) => {
  if (!id) return true; // New subscriber - all fields editable
  return hasPermission(fieldName, "edit", true); // Default true for backward compatibility
};

const isFieldVisible = (fieldName) => {
  return hasPermission(fieldName, "view", true); // Default true for backward compatibility
};

// Permission-aware table row component for editable fields
const PermissionTableRow = ({
  fieldName,
  label,
  isEditing,
  onToggleEdit,
  children,
  id,
  colSpan = "2",
  rightContent = null,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = isFieldEditable(id, fieldName);
  const hasHandler = typeof onToggleEdit === "function";
  const handleClick =
    canEdit && hasHandler
      ? () => onToggleEdit(fieldName.replace("subscriber_", ""))
      : undefined;
  const actualFieldName = fieldName.replace("subscriber_", ""); // Remove prefix for onClick

  return (
    <tr>
      <td width={rightContent ? "140" : ""}>{label}</td>
      <td
        className={canEdit && hasHandler ? "edtext" : ""}
        colSpan={rightContent ? "1" : colSpan}
        data-id={actualFieldName}
        onClick={handleClick}
        style={{ cursor: canEdit && hasHandler ? "pointer" : "default" }}
      >
        {children}
      </td>
      {rightContent && (
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {rightContent}
        </td>
      )}
    </tr>
  );
};

// Permission-aware input component for inline editing
const PermissionEditableField = ({
  fieldName,
  value,
  isEditing,
  editFieldName,
  onChange,
  onBlur,
  onKeyDown,
  type = "text",
  placeholder = "",
  id,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = isFieldEditable(id, fieldName);

  if (isEditing === editFieldName && canEdit) {
    return (
      <span className="edit">
        <input
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus
        />
      </span>
    );
  }

  return <span className="value">{value}</span>;
};

// Permission-aware dropdown component for inline editing
const PermissionEditableDropdown = ({
  fieldName,
  value,
  isEditing,
  editFieldName,
  onChange,
  options = [],
  optionValue = "id",
  optionLabel = "pick_name",
  placeholder = "Select...",
  id,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = isFieldEditable(id, fieldName);

  if (isEditing === editFieldName && canEdit) {
    return (
      <span className="edit">
        <select value={value} onChange={onChange}>
          <option value="">{placeholder}</option>
          {options.map((item, index) => (
            <option key={index} value={item[optionValue]}>
              {item[optionLabel]}
            </option>
          ))}
        </select>
      </span>
    );
  }

  // Display selected value
  const selectedItem = options.find((item) => item[optionValue] == value);
  const displayValue = selectedItem ? selectedItem[optionLabel] : value;

  return <span className="value">{displayValue}</span>;
};

const FIELD_CONFIG = {
  subscriber: { formKey: "subscriber", payloadField: "subscriberid" },
  state_code: { formKey: "state_code", payloadField: "state_code" },
  old_discount_amt: {
    formKey: "old_discount_amt",
    payloadField: "discount_amt",
    syncFields: ["discount_amt"],
  },
  discount_amt: {
    formKey: "discount_amt",
    payloadField: "discount_amt",
    syncFields: ["old_discount_amt"],
  },
  actual_amount: { formKey: "actual_amount", payloadField: "actual_amount" },
  order_approval: { formKey: "order_approval", payloadField: "order_approval" },
  order_due_date: { formKey: "order_due_date", payloadField: "order_due_date" },
  sales_manager: { formKey: "sales_manager", payloadField: "sales_manager" },
  upgraded_by: { formKey: "upgraded_by", payloadField: "upgraded_by" },
  order_status: { formKey: "order_status", payloadField: "order_status" },
  refund_date: { formKey: "refund_date", payloadField: "refund_date" },
  refund_details: { formKey: "refund_details", payloadField: "refund_details" },
};

const getFieldConfig = (fieldName) => {
  return FIELD_CONFIG[fieldName] || { formKey: fieldName, payloadField: fieldName };
};

const normalizeValueForCompare = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  return String(value).trim();
};

const OrderEdit = () => {
  const { id } = useParams();
  const location = useLocation();
  const filters = location.state?.filterParams || {};
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const [previousId, setPreviousId] = useState(null);
  const [nextId, setNextId] = useState(null);

  const [order, setOrder] = useState([]);
  const [orderSubData, setOrderSubData] = useState([]);
  const [activityList, setActivityList] = useState([]);
  const [parentOrderId, setParentOrderId] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);

  const [loading, setLoading] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [sendingInvoiceId, setSendingInvoiceId] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const tableRenderedRef = useRef(false);

  const [dropdownOptions, setDropdownOptions] = useState({
    offerTypeList: [],
    orderTypeList: [],
    orderSubscriptionTypeList: [],
    orderApprovallist: [],
    salesManagerList: [],
    upgradedByList: [],
    amountlist: [],
    refundStatus: [],
    stateList: [],
  });

  const [isModalSubscriberOpen, setisModalSubscriberOpen] = useState(false);
  const [isAssignParentOrderModalOpen, setIsAssignParentOrderModalOpen] =
    useState(false);

  const toggleModal = () => {
    setisModalSubscriberOpen(!isModalSubscriberOpen);
  };

  const closeModal = () => {
    setisModalSubscriberOpen(false);
  };

  const openAssignParentOrderModal = () => {
    setIsAssignParentOrderModalOpen(true);
  };

  const closeAssignParentOrderModal = () => {
    setIsAssignParentOrderModalOpen(false);
  };

  const handleAssignParentOrderSuccess = () => {
    fetchData(); // Refresh the order data
  };

  const [editField, setEditField] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});

  useEffect(() => {
    if (order && Object.keys(order).length > 0) {
      const nextFormValues = {
        old_discount_amt: order.discount_amt || 0,
        discount_amt: order.discount_amt || 0,
        actual_amount: order.actual_amount || 0,
        order_approval:
          order.order_approval !== undefined ? String(order.order_approval) : "",
        order_due_date: order.order_due_date || "",
        sales_manager:
          order.sales_manager_user_id !== undefined &&
            order.sales_manager_user_id !== null
            ? String(order.sales_manager_user_id)
            : "",
        upgraded_by:
          order.upgraded_by_user_id !== undefined &&
            order.upgraded_by_user_id !== null
            ? String(order.upgraded_by_user_id)
            : "",
        subscriber:
          order.subscriberid !== undefined && order.subscriberid !== null
            ? String(order.subscriberid)
            : "",
        order_status:
          order.order_status !== undefined && order.order_status !== null
            ? String(order.order_status)
            : "",
        refund_date: order.refund_date || "",
        refund_details: order.refund_details || "",
        state_code:
          order.state_code !== undefined && order.state_code !== null
            ? String(order.state_code)
            : "",
      };
      setFormValues(nextFormValues);
      setOriginalValues((prev) => ({
        ...prev,
        ...nextFormValues,
      }));
    }
  }, [order]);

  const handleChange = (field, value) => {
    const { formKey, syncFields = [] } = getFieldConfig(field);
    setFormValues((prev) => {
      const updated = { ...prev, [formKey]: value };
      syncFields.forEach((key) => {
        updated[key] = value;
      });
      return updated;
    });
  };

  const handleSave = async (field, overrideValue = undefined) => {
    const { formKey, payloadField, syncFields = [] } = getFieldConfig(field);
    const currentValue =
      overrideValue !== undefined ? overrideValue : formValues[formKey];
    const originalValue = originalValues[formKey];

    if (
      currentValue === undefined ||
      normalizeValueForCompare(currentValue) ===
      normalizeValueForCompare(originalValue)
    ) {
      setEditField(null);
      return;
    }

    setLoading(true);
    const payload = {
      id,
      fieldname: payloadField,
      fieldvalue: currentValue,
    };

    try {
      const response = await axios.put(`${API_URL}orders/edit`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        showToast.success("Updated Successfully");
        setOriginalValues((prev) => {
          const updated = { ...prev, [formKey]: currentValue };
          syncFields.forEach((key) => {
            updated[key] = currentValue;
          });
          return updated;
        });
        setFormValues((prev) => {
          const updated = { ...prev, [formKey]: currentValue };
          syncFields.forEach((key) => {
            updated[key] = currentValue;
          });
          return updated;
        });
        setEditField(null);
        await fetchData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error updating field:", error);
      showToast.error("Failed to update field");
      setLoading(false);
    }
  };

  // Helper: store original value when entering edit mode
  const handleToggleEdit = (fieldName) => {
    const { formKey } = getFieldConfig(fieldName);
    setOriginalValues((prev) => ({
      ...prev,
      [formKey]: formValues[formKey],
    }));
    setEditField(fieldName);
  };

  // Helper: revert form value to original
  const revertFieldValue = (fieldName) => {
    const { formKey, syncFields = [] } = getFieldConfig(fieldName);
    const originalValue = originalValues[formKey];
    setFormValues((prev) => {
      const updated = { ...prev, [formKey]: originalValue };
      syncFields.forEach((key) => {
        updated[key] = originalValue;
      });
      return updated;
    });
    setEditField(null);
  };

  // On blur: if unchanged or empty, revert and close; else keep to be saved by caller
  const handleBlur = (fieldName) => {
    const { formKey } = getFieldConfig(fieldName);
    const currentValue = formValues[formKey];
    const originalValue = originalValues[formKey];
    if (
      normalizeValueForCompare(currentValue) ===
      normalizeValueForCompare(originalValue) ||
      currentValue === "" ||
      currentValue === null
    ) {
      revertFieldValue(fieldName);
      return;
    }
    setEditField(null);
  };

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00") return "";
    return moment(dateStr).format("DD-MM-YYYY");
  };

  // Format datetime for display
  const formatDateTimeDisplay = (datetimeStr) => {
    if (!datetimeStr || datetimeStr === "0000-00-00 00:00:00") return "";
    return moment(datetimeStr).format("ddd MMM DD YYYY HH:mm A");
  };

  const checkRenderingStatus = () => {
    if (tableRenderedRef.current) {
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setIsRendering(true);
    tableRenderedRef.current = false;
    try {
      // Exclude sortField and sortOrder from filters as they are not needed for the edit API
      const { sortField, sortOrder, ...apiFilters } = filters;

      const response = await axios.post(
        `${API_URL}orders/info/`,
        apiFilters, // Send filters in body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            accept: "*/*",
          },
          params: {
            id: id,
          },
        }
      );

      const pendingOrdersList = response.data.pendingOrders || [];
      const currentPending = pendingOrdersList.find((p) => String(p.id) === String(id));
      const orderData = {
        ...(response.data.orderdata || {}),
        state_code:
          response.data.orderdata?.state_code !== undefined &&
          response.data.orderdata?.state_code !== null
            ? response.data.orderdata.state_code
            : currentPending?.state_code,
      };
      const orderSubData = response.data.order_sub_data || [];
      const activity = response.data.activitylist || [];

      setOrder(orderData);
      setOrderSubData(orderSubData);
      setActivityList(activity);
      setParentOrderId(response.data.parentOrderId || null);
      setPendingOrders(pendingOrdersList);
      setPreviousId(response.data.previousid);
      setNextId(response.data.nextid);

      // Update dropdown options from API response
      setDropdownOptions(prev => ({
        ...prev,
        amountlist: Object.entries(response.data.amountlist || {}).map(([value, label]) => ({
          value,
          label,
        })),
        salesManagerList: Object.entries(response.data.salesmanagerlist || {}).map(([tu_id, tu_user_fullname]) => ({
          tu_id,
          tu_user_fullname,
        })),
        upgradedByList: Object.entries(response.data.upgradedbylist || {}).map(([id, user_fullname]) => ({
          id,
          user_fullname,
        })),
      }));

      setTimeout(() => {
        tableRenderedRef.current = true;
        checkRenderingStatus();
      }, 100);

    } catch (error) {
      console.error("Error fetching subscription data:", error);
      showToast.error("Failed to load order details");
      tableRenderedRef.current = true;
      checkRenderingStatus();
    } finally {
      setLoading(false);
    }
  };

  const fetchAdditionalDropdownData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}orders/get-dropdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success && response.data.data) {
        setDropdownOptions(prev => ({
          ...prev,
          offerTypeList: response.data.data.offertypelist || [],
          orderTypeList: response.data.data.ordertypelist || [],
          orderSubscriptionTypeList:
            response.data.data.orderSubscriptionTypelist || [],
          orderApprovallist: response.data.data.orderApprovallist || [],
          salesManagerList: response.data.data.sales_manager_userlist || [],
          refundStatus: response.data.data.refundStatus || [],
          stateList: response.data.data.statelist || [],
          // Don't override amountlist, upgradedByList here
          // as they come from the main API
        }));
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Only fetch dropdown data that's not available in the main API response
    fetchAdditionalDropdownData();
  }, [id]);


  useEffect(() => {
    fetchAdditionalDropdownData();
  }, []);

  const [subscriberOptions, setSubscriberOptions] = useState([]);
  const [subscriberSearch, setSubscriberSearch] = useState("");

  // Load options dynamically
  const loadOptions = async (inputValue, callback) => {
    if (inputValue.length < 3) {
      callback([]); // minimum input length = 3
      return;
    }

    try {
      const response = await axios.get(`${API_URL}orders/subscriber`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { search: inputValue },
      });

      const options = response.data.map((item) => ({
        label: `${item.fullname} (${item.subscriberid}) (${item.mobileno})`,
        value: String(item.id),
      }));

      callback(options);
    } catch (error) {
      console.error("Error fetching subscriber data:", error);
      callback([]);
    }
  };

  useEffect(() => {
    if (formValues.subscriber) {
      axios
        .get(`${API_URL}orders/subscriber/${formValues.subscriber}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          const s = res.data;
          const formatted = {
            value: String(s.id),
            label: `${s.fullname} (${s.subscriberid}) (${s.mobileno})`,
          };

          // ✅ Only add if it’s not already in subscriberOptions
          setSubscriberOptions((prev) => {
            const exists = prev.some((opt) => opt.value === formatted.value);
            if (!exists) return [...prev, formatted];
            return prev;
          });
        })
        .catch((error) => {
          console.error("Error fetching subscriber:", error);
        });
    }
  }, [formValues.subscriber]);


  const handleAsyncSelectChange = (selectedOption, { name }) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (selectedOption) {
      setSubscriberOptions((prev) => {
        const exists = prev.some((opt) => opt.value === selectedOption.value);
        if (!exists) {
          return [...prev, selectedOption];
        }
        return prev;
      });
    }
  };


  const handleDownloadInvoice = async (orderId, serviceName) => {
    try {
      setDownloadingInvoiceId(orderId);
      const token = localStorage.getItem("authToken");

      const response = await axios.get(`${API_URL}orders/download/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob", // 👈 necessary for file downloads
      });

      // Create blob and trigger download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });

      const contentDisposition = response.headers["content-disposition"];
      // Use serviceName if available, fallback to orderId
      const safeServiceName = serviceName
        ? serviceName.replace(/[^a-zA-Z0-9_\-]/g, "_")
        : orderId;
      let fileName = `Invoice-${safeServiceName}.pdf`; // Default fallback filename

      if (contentDisposition && contentDisposition.includes("filename=")) {
        fileName = contentDisposition
          .split("filename=")[1]
          .split(";")[0]
          .replace(/"/g, "");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Download invoice error:", error);
      showToast.error("Failed to download invoice");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };


  const handleSendInvoice = async (orderId) => {
    try {
      setSendingInvoiceId(orderId);
      const token = localStorage.getItem("authToken");

      const response = await axios.post(
        `${API_URL}orders/sendInvoice/${orderId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        showToast.success(response.data.message || "Invoice sent successfully");
      } else {
        showToast.error(response.data.message || "Failed to send invoice");
      }
    } catch (error) {
      console.error("Send invoice error:", error);
      showToast.error("An error occurred while sending the invoice");
    } finally {
      setSendingInvoiceId(null);
    }
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      {loading}
      <div id="content" className="contentrrrrrrr">
        <div className="row pb-2">
          <div className="col-md-4">
            <img
              src={profileImg}
              alt="Profile"
              style={{ width: "32px", marginRight: "10px" }}
            />
            {hasPermission("subscriber_fullname", "view") &&
              (hasPermission("subscriber", "edit") ? (
                <Link to={`/Subscriber/Edit/${order.subscriberid}`}>
                  <span id="subscribername">{order.subscriberfullname}</span>
                </Link>
              ) : (
                <span id="subscribername">{order.subscriberfullname}</span>
              ))}
            {order.verifyUser === 1 && (
              <img
                src={blueTick}
                alt="Verified"
                style={{
                  width: "20px",
                  marginTop: "-10px",
                  marginLeft: "5px",
                }}
              />
            )}
            {hasPermission("subscriber_mobileno", "view") && (
              <>
                <FontAwesomeIcon
                  icon={faMobileAlt}
                  className={order.token ? "text-success" : "text-danger"}
                  style={{
                    fontSize: "1.5rem",
                    lineHeight: "1.5rem",
                    marginLeft: "5px",
                  }}
                />{" "}
                {order.subscribermobile}
              </>
            )}
          </div>
          <div className="col-md-8" style={{ textAlign: "right" }}>
            {nextId ? (
              <button
                className="btn btn-default btn-sm pull-right mx-1"
                onClick={() => navigate(`/Order/edit/${nextId}`, { state: { filterParams: filters } })}
                title="Next Order"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            ) : null}
            {previousId ? (
              <button
                className="btn btn-default btn-sm pull-right mx-1"
                onClick={() => navigate(`/Order/edit/${previousId}`, { state: { filterParams: filters } })}
                title="Previous Order"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            ) : null}
            <button
              className="btn btn-default btn-sm pull-right ml-1"
              onClick={() => navigate("/Order/")}
              title="Go Back To Orders"
            >
              <i className="fa fa-chevron-circle-left"></i> Go Back
            </button>
            <button
              className="btn btn-primary btn-sm me-2"
              title="Download Invoice"
              onClick={() => handleDownloadInvoice(order.id, order.service_name || order.serviceName)}
              disabled={downloadingInvoiceId === order.id}
            >
              <FontAwesomeIcon
                icon={
                  downloadingInvoiceId === order.id ? faSpinner : faDownload
                }
                spin={downloadingInvoiceId === order.id}
              />{" "}
              {downloadingInvoiceId === order.id
                ? "Downloading..."
                : "Download Invoice"}
            </button>
            <button
              className="btn btn-default btn-sm me-2"
              title="Send Invoice"
              onClick={() => handleSendInvoice(order.id)}
              disabled={sendingInvoiceId === order.id}
            >
              <FontAwesomeIcon
                icon={
                  sendingInvoiceId === order.id ? faSpinner : faEnvelope
                }
                spin={sendingInvoiceId === order.id}
              />{" "}
              {sendingInvoiceId === order.id ? "Sending..." : "Send Invoice"}
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-md-5">
            <div className="panel panel-default with-rounded-corner with-shadow">
              <div className="panel-body">
                <table style={{ fontSize: "11px", color: "#808080" }}>
                  <tbody>
                    <tr>
                      <td>
                        Created On :{" "}
                        {order.created_on
                          ? formatDateTimeDisplay(order.created_on)
                          : order.created_at
                            ? formatDateTimeDisplay(order.created_at)
                            : ""}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table className="table table-condensed table-hover bg-white usertable">
                  <tbody id="userDetailTable">
                    {parentOrderId && (
                      <tr>
                        <td width="140">Parent Order ID</td>
                        <td>
                          {loading || isRendering ? (
                            <div
                              className="skeleton-text"
                              style={{
                                width: "100%",
                                height: "12px",
                                backgroundColor: "#e0e0e0",
                              }}
                            ></div>
                          ) : (
                            <Link to={`/Order/Edit/${order.parent}`}>
                              {parentOrderId}
                            </Link>
                          )}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td width="140">Order ID</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          order.orderid
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td width="140">Subscriber ID</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          <>
                            {order.subscriberorgid}
                            <button
                              className="btn btn-xs btn-primary float-end btnUpdateSubscriber"
                              onClick={toggleModal}
                              title="Update Subscriber"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td width="140">Research Fee</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          order.research_fee
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td width="140">Tax Amount</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          order.tax_amt
                        )}
                      </td>
                    </tr>
                    <PermissionTableRow
                      fieldName="order_paid_amount"
                      label="Paid Amount"
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : (
                        order.amount_payable
                      )}
                    </PermissionTableRow>

                    {/* Paid Amount (editable) */}
                    <PermissionTableRow
                      fieldName="order_paid_amount"
                      label="Paid Amount"
                      isEditing={editField === "old_discount_amt"}
                      onToggleEdit={() => handleToggleEdit("old_discount_amt")}
                      id={id}
                      colSpan="2"
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "old_discount_amt" ? (
                        <input
                          type="text"
                          className="form-control"
                          value={formValues.old_discount_amt}
                          onChange={(e) =>
                            handleChange("old_discount_amt", e.target.value)
                          }
                          onBlur={(e) => {
                            const nextValue = e.target.value;
                            if (
                              nextValue === "" ||
                              nextValue ===
                              originalValues.old_discount_amt
                            ) {
                              handleBlur("old_discount_amt");
                            } else {
                              handleSave("old_discount_amt", nextValue);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const nextValue = e.target.value;
                              if (
                                nextValue === "" ||
                                nextValue ===
                                originalValues.old_discount_amt
                              ) {
                                handleBlur("old_discount_amt");
                              } else {
                                handleSave("old_discount_amt", nextValue);
                              }
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <span>{formValues.discount_amt}</span>
                      )}
                    </PermissionTableRow>

                    <PermissionTableRow
                      fieldName="order_actual_amount"
                      label="Actual Amount"
                      isEditing={editField === "actual_amount"}
                      onToggleEdit={() => handleToggleEdit("actual_amount")}
                      id={id}
                      colSpan="2"
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "actual_amount" ? (
                        <select
                          className="form-control"
                          value={formValues.actual_amount}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            handleChange("actual_amount", newValue);

                            // ✅ Call handleSave immediately with the new value
                            if (newValue !== originalValues.actual_amount) {
                              handleSave("actual_amount", newValue);
                            }
                          }}
                          onBlur={() => handleBlur("actual_amount")}
                          autoFocus
                        >
                          <option value="">Select</option>
                          {dropdownOptions.amountlist.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {
                            dropdownOptions.amountlist.find(
                              (opt) => opt.value == formValues.actual_amount
                            )?.label
                          }
                        </span>
                      )}
                    </PermissionTableRow>


                    {/* Discount Amount */}
                    <PermissionTableRow
                      fieldName="order_discount_amount"
                      label="Discount Amount"
                      isEditing={editField === "discount_amt"}
                      onToggleEdit={() => handleToggleEdit("discount_amt")}
                      id={id}
                      colSpan="2"
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "discount_amt" ? (
                        <input
                          type="text"
                          className="form-control"
                          value={formValues.discount_amt}
                          onChange={(e) =>
                            handleChange("discount_amt", e.target.value)
                          }
                          onBlur={(e) => {
                            const nextValue = e.target.value;
                            if (
                              nextValue === "" ||
                              nextValue === originalValues.discount_amt
                            ) {
                              handleBlur("discount_amt");
                            } else {
                              handleSave("discount_amt", nextValue);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const nextValue = e.target.value;
                              if (
                                nextValue === "" ||
                                nextValue === originalValues.discount_amt
                              ) {
                                handleBlur("discount_amt");
                              } else {
                                handleSave("discount_amt", nextValue);
                              }
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <span>{formValues.discount_amt}</span>
                      )}
                    </PermissionTableRow>

                    <PermissionTableRow
                      fieldName="remaining_amount" // Permission key
                      label="Remaining Amount"
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : (
                        <span id="remaining_amtid">{order.remaining_amt}</span>
                      )}
                    </PermissionTableRow>
                    {/* Discount Type with Permission */}
                    <PermissionTableRow
                      fieldName="discount_type"
                      label="Discount Type"
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : (
                        dropdownOptions.offerTypeList.find(
                          (option) => option.value == order.offer_type
                        )?.label
                      )}
                    </PermissionTableRow>

                    {/* Discount Code with Permission */}
                    <PermissionTableRow
                      fieldName="discount_code"
                      label="Discount Code"
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : (
                        order.offercode
                      )}
                    </PermissionTableRow>
                    <tr>
                      <td width="140">Order Type</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          <>
                            {!parentOrderId ? "Main" : "Pending"}{" "}
                            {!parentOrderId && pendingOrders.length == 1 && (
                              <button
                                className="btn btn-xs btn-primary float-end btnAssignParentOrder"
                                title="Edit Order Type"
                                onClick={openAssignParentOrderModal}
                              >
                                <FontAwesomeIcon icon={faPencil} />
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                    <PermissionTableRow
                      fieldName="order_order_approval"
                      label="Order Approval"
                      isEditing={editField === "order_approval"}
                      onToggleEdit={() => handleToggleEdit("order_approval")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "order_approval" ? (
                        <select
                          className="form-control"
                          value={formValues.order_approval}
                          autoFocus
                          onChange={async (e) => {
                            const newValue = e.target.value;

                            // 1️⃣ Update local state
                            handleChange("order_approval", newValue);

                            // 2️⃣ Only save if value changed
                            if (newValue !== originalValues.order_approval) {
                              await handleSave("order_approval", newValue);
                            }
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              await handleSave("order_approval", e.target.value);
                            }
                          }}
                        >
                          <option value="">Select</option>
                          {dropdownOptions.orderApprovallist.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          style={{
                            background: "#007aff",
                            color: "#fff",
                            display: "block",
                            padding: "8px",
                          }}
                        >
                          {
                            dropdownOptions.orderApprovallist.find(
                              (opt) => opt.value == formValues.order_approval
                            )?.label
                          }
                        </span>
                      )}
                    </PermissionTableRow>



                    <tr>
                      <td width="140">Order Created By</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          order.createdbyname
                        )}
                      </td>
                    </tr>

                    <tr>
                      <td width="140">Bill To</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          order.bill_to
                        )}
                      </td>
                    </tr>
                    <PermissionTableRow
                      fieldName="order_state"
                      label="State"
                      isEditing={editField === "state_code"}
                      onToggleEdit={() => handleToggleEdit("state_code")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "state_code" ? (
                        <select
                          className="form-control"
                          value={formValues.state_code || ""}
                          autoFocus
                          onChange={async (e) => {
                            const newValue = e.target.value;
                            handleChange("state_code", newValue);
                            if (newValue !== originalValues.state_code) {
                              await handleSave("state_code", newValue);
                            }
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              await handleSave("state_code", e.target.value);
                            }
                          }}
                        >
                          <option value="">Select State</option>
                          {(dropdownOptions.stateList || []).map((opt) => (
                            <option
                              key={opt.stateId || opt.state_id || opt.id}
                              value={opt.stateCode || opt.state_code}
                            >
                              {opt.stateName || opt.state_name || opt.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {(() => {
                            const currentCode =
                              formValues.state_code || order.state_code;
                            const selectedState = (dropdownOptions.stateList || []).find(
                              (opt) =>
                                Number(opt.stateCode ?? opt.state_code) === Number(currentCode) ||
                                Number(opt.stateId ?? opt.state_id) === Number(currentCode)
                            );
                            return selectedState
                              ? (selectedState.stateName || selectedState.state_name)
                              : "Not Set";
                          })()}
                        </span>
                      )}
                    </PermissionTableRow>
                    <tr>
                      <td width="140">GST No.</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          order.gstno
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td width="140">Payment Mode</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          order.payment_mode
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td width="140">Payment Date</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : order.payment_date ? (
                          formatDateDisplay(order.payment_date)
                        ) : (
                          formatDateDisplay(order.created_on)
                        )}
                      </td>
                    </tr>
                    <PermissionTableRow
                      fieldName="order_due_date"
                      label="Order Due Date"
                      isEditing={editField === "order_due_date"}
                      onToggleEdit={() => handleToggleEdit("order_due_date")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "order_due_date" ? (
                        <DatePicker
                          selected={
                            formValues.order_due_date
                              ? new Date(formValues.order_due_date)
                              : null
                          }
                          onChange={(date) =>
                            handleChange(
                              "order_due_date",
                              moment(date).format("YYYY-MM-DD")
                            )
                          }
                          onBlur={() => {
                            if (
                              formValues.order_due_date === "" ||
                              formValues.order_due_date ===
                              originalValues.order_due_date
                            ) {
                              handleBlur("order_due_date");
                            } else {
                              handleSave("order_due_date");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.order_due_date === "" ||
                                formValues.order_due_date ===
                                originalValues.order_due_date
                              ) {
                                handleBlur("order_due_date");
                              } else {
                                handleSave("order_due_date");
                              }
                            }
                          }}
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                          autoFocus
                          style={{ background: "#007aff", color: "#fff" }}
                        />
                      ) : (
                        <span
                          style={{
                            background: "#007aff",
                            color: "#fff",
                            display: "block",
                            padding: "8px",
                          }}
                        >
                          {formatDateDisplay(formValues.order_due_date)}
                        </span>
                      )}
                    </PermissionTableRow>
                    <PermissionTableRow
                      fieldName="order_status"
                      label="Order Status"
                      isEditing={editField === "order_status"}
                      onToggleEdit={() => handleToggleEdit("order_status")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "order_status" ? (
                        <select
                          className="form-control"
                          value={formValues.order_status}
                          onChange={async (e) => {
                            const nextValue = e.target.value;
                            handleChange("order_status", nextValue);
                            if (
                              normalizeValueForCompare(nextValue) !==
                              normalizeValueForCompare(
                                originalValues.order_status
                              )
                            ) {
                              await handleSave("order_status", nextValue);
                            }
                          }}
                          onBlur={() => handleBlur("order_status")}
                          onKeyDown={async (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              await handleSave("order_status", e.target.value);
                            }
                          }}
                          autoFocus
                        >
                          <option value="">Select Refund Status</option>
                          {dropdownOptions.refundStatus.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="value">
                          {dropdownOptions.refundStatus.find(
                            (opt) => opt.value == formValues.order_status
                          )?.label || "Not set"}
                        </span>
                      )}
                    </PermissionTableRow>
                    {/* Add Refund Date Field */}
                    <PermissionTableRow
                      fieldName="refund_date"
                      label="Refund Date"
                      isEditing={editField === "refund_date"}
                      onToggleEdit={() => handleToggleEdit("refund_date")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "refund_date" ? (
                        <DatePicker
                          selected={
                            formValues.refund_date
                              ? new Date(formValues.refund_date)
                              : null
                          }
                          onChange={(date) =>
                            handleChange(
                              "refund_date",
                              moment(date).format("YYYY-MM-DD")
                            )
                          }
                          onBlur={() => {
                            if (
                              formValues.refund_date === "" ||
                              formValues.refund_date === originalValues.refund_date
                            ) {
                              handleBlur("refund_date");
                            } else {
                              handleSave("refund_date");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.refund_date === "" ||
                                formValues.refund_date === originalValues.refund_date
                              ) {
                                handleBlur("refund_date");
                              } else {
                                handleSave("refund_date");
                              }
                            }
                          }}
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                          autoFocus
                        />
                      ) : (
                        <span className="value">
                          {formatDateDisplay(formValues.refund_date)}
                        </span>
                      )}
                    </PermissionTableRow>

                    {/* Add Refund Details Field */}
                    <PermissionTableRow
                      fieldName="refund_details"
                      label="Refund Details"
                      isEditing={editField === "refund_details"}
                      onToggleEdit={() => handleToggleEdit("refund_details")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "refund_details" ? (
                        <input
                          type="text"
                          className="form-control"
                          value={formValues.refund_details || ""}
                          onChange={(e) =>
                            handleChange("refund_details", e.target.value)
                          }
                          onBlur={(e) => {
                            const nextValue = e.target.value;
                            if (
                              nextValue === "" ||
                              nextValue === originalValues.refund_details
                            ) {
                              handleBlur("refund_details");
                            } else {
                              handleSave("refund_details", nextValue);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                e.target.value === "" ||
                                e.target.value === originalValues.refund_details
                              ) {
                                handleBlur("refund_details");
                              } else {
                                handleSave("refund_details", e.target.value);
                              }
                            }
                          }}
                          placeholder="Enter refund details"
                          autoFocus
                        />
                      ) : (
                        <span className="value">
                          {formValues.refund_details || ""}
                        </span>
                      )}
                    </PermissionTableRow>
                    <tr>
                      <td width="140">Transaction ID</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          order.transactionid
                        )}
                      </td>
                    </tr>
                    <PermissionTableRow
                      fieldName="assign_to"
                      label="Assigned To"
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : (
                        order.assigned_user_name
                      )}
                    </PermissionTableRow>
                    <PermissionTableRow
                      fieldName="order_sales_manager"
                      label="Sales Manager"
                      isEditing={editField === "sales_manager"}
                      onToggleEdit={() => handleToggleEdit("sales_manager")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "sales_manager" ? (
                        <select
                          className="form-control"
                          value={formValues.sales_manager}
                          onChange={async (e) => {
                            const nextValue = e.target.value;
                            handleChange("sales_manager", nextValue);
                            if (
                              normalizeValueForCompare(nextValue) !==
                              normalizeValueForCompare(
                                originalValues.sales_manager
                              )
                            ) {
                              await handleSave("sales_manager", nextValue);
                            }
                          }}
                          onBlur={() => handleBlur("sales_manager")}
                          onKeyDown={async (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              await handleSave("sales_manager", e.target.value);
                            }
                          }}
                          autoFocus
                          style={{ background: "#007aff", color: "#fff" }}
                        >
                          <option value="">Select</option>
                          {dropdownOptions.salesManagerList && dropdownOptions.salesManagerList.map((opt) => (
                            <option
                              key={opt.tu_id || opt.id || opt.value}
                              value={String(
                                opt.tu_id || opt.id || opt.value
                              )}
                            >
                              {opt.tu_user_fullname ||
                                opt.user_fullname ||
                                opt.label ||
                                `User ${opt.tu_id || opt.id || ""}`}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          style={{
                            background: "#007aff",
                            color: "#fff",
                            display: "block",
                            padding: "8px",
                          }}
                        >
                          {(() => {
                            const opt = dropdownOptions.salesManagerList?.find(
                              (o) => (o.tu_id || o.id || o.value) == formValues.sales_manager
                            );
                            return opt ? (opt.tu_user_fullname || opt.user_fullname || opt.label) : "Not assigned";
                          })()}
                        </span>
                      )}
                    </PermissionTableRow>
                    <PermissionTableRow
                      fieldName="order_upgraded_by"
                      label="Upgraded By"
                      isEditing={editField === "upgraded_by"}
                      onToggleEdit={() => handleToggleEdit("upgraded_by")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "upgraded_by" ? (
                        <select
                          className="form-control"
                          value={formValues.upgraded_by}
                          onChange={(e) =>
                            handleChange("upgraded_by", e.target.value)
                          }
                          onBlur={(e) => {
                            const nextValue = e.target.value;
                            if (
                              nextValue === "" ||
                              nextValue === originalValues.upgraded_by
                            ) {
                              handleBlur("upgraded_by");
                            } else {
                              handleSave("upgraded_by", nextValue);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const nextValue = e.target.value;
                              if (
                                nextValue === "" ||
                                nextValue === originalValues.upgraded_by
                              ) {
                                handleBlur("upgraded_by");
                              } else {
                                handleSave("upgraded_by", nextValue);
                              }
                            }
                          }}
                          autoFocus
                          style={{ background: "#007aff", color: "#fff" }}
                        >
                          <option value="">- Select -</option>
                          {dropdownOptions.upgradedByList
                            ? dropdownOptions.upgradedByList.map((opt) => (
                              <option key={opt.value} value={opt.id}>
                                {opt.user_fullname}
                              </option>
                            ))
                            : ""}
                        </select>
                      ) : (
                        <span
                          style={{
                            background: "#007aff",
                            color: "#fff",
                            display: "block",
                            padding: "8px",
                          }}
                        >
                          {dropdownOptions.upgradedByList
                            ? dropdownOptions.upgradedByList.find(
                              (opt) => opt.id == formValues.upgraded_by
                            )?.user_fullname
                            : ""}
                        </span>
                      )}
                    </PermissionTableRow>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="col-md-7">
            <div className="row">
              <div className="col-md-12">
                <div className="panel  panel-default with-rounded-corner with-shadow perform-detail p-10">
                  <div className="box">
                    <div className="row">
                      <table className="table table-condensed table-hover bg-white mb-0">
                        <thead>
                          <tr>
                            <th>Sr.</th>
                            <th>Service</th>
                            <th>Trade</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading || isRendering ? (
                            [...Array(3)].map((_, i) => (
                              <tr key={i} className="skeleton-row">
                                <td><div className="skeleton-text" style={{ width: "20px" }}></div></td>
                                <td><div className="skeleton-text" style={{ width: "150px" }}></div></td>
                                <td><div className="skeleton-text" style={{ width: "80px" }}></div></td>
                                <td><div className="skeleton-text" style={{ width: "60px" }}></div></td>
                              </tr>
                            ))
                          ) : orderSubData.length > 0 && orderSubData ? (
                            orderSubData.map((order, index) => {
                              return (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{order.service_name}</td>
                                  <td>{order.trade}</td>
                                  <td>{order.price}</td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center">
                                No Records
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-md-12">
                <h4>Pending Orders</h4>
                <div className="panel  panel-default with-rounded-corner with-shadow perform-detail p-10">
                  <div className="box">
                    <div className="row">
                      <table className="table table-condensed table-hover bg-white mb-0">
                        <thead>
                          <tr>
                            <th>Sr.</th>
                            <th>OrderId</th>
                            <th>Payment Date</th>
                            <th>Paid Amount</th>
                            <th>Transaction Id</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading || isRendering ? (
                            [...Array(2)].map((_, i) => (
                              <tr key={i} className="skeleton-row">
                                <td><div className="skeleton-text" style={{ width: "20px" }}></div></td>
                                <td><div className="skeleton-text" style={{ width: "100px" }}></div></td>
                                <td><div className="skeleton-text" style={{ width: "100px" }}></div></td>
                                <td><div className="skeleton-text" style={{ width: "80px" }}></div></td>
                                <td><div className="skeleton-text" style={{ width: "150px" }}></div></td>
                                <td><div className="skeleton-text" style={{ width: "80px" }}></div></td>
                              </tr>
                            ))
                          ) : (
                            pendingOrders.map((order, index) => {
                              return (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <Link to={`/Order/Edit/${order.id}`}>
                                      {order.orderid}
                                    </Link>
                                  </td>
                                  <td>
                                    {order.payment_date
                                      ? formatDateDisplay(order.payment_date)
                                      : "N/A"}
                                  </td>
                                  <td>{order.amount_payable}</td>
                                  <td>{order.transactionid}</td>
                                  <td>
                                    <button
                                      className="btn btn-default btn-sm ms-1"
                                      title="Download Invoice"
                                      onClick={() =>
                                        handleDownloadInvoice(order.id, order.service_name || order.serviceName)
                                      }
                                      disabled={downloadingInvoiceId === order.id}
                                    >
                                      <FontAwesomeIcon
                                        icon={
                                          downloadingInvoiceId === order.id
                                            ? faSpinner
                                            : faDownload
                                        }
                                        spin={downloadingInvoiceId === order.id}
                                      />
                                    </button>

                                    <button
                                      className="btn btn-default btn-sm ms-1"
                                      title="Send Invoice"
                                      onClick={() => handleSendInvoice(order.id)}
                                      disabled={sendingInvoiceId === order.id}
                                    >
                                      <FontAwesomeIcon
                                        icon={
                                          sendingInvoiceId === order.id
                                            ? faSpinner
                                            : faEnvelope
                                        }
                                        spin={sendingInvoiceId === order.id}
                                      />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                        <tbody>
                          <tr>
                            <th colSpan="3">Total Order Amount</th>
                            <th colSpan="3">
                              {pendingOrders
                                .reduce(
                                  (sum, order) =>
                                    sum + Number(order.amount_payable || 0),
                                  0
                                )
                                .toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                            </th>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-md-12">
                <div className="panel panel-default with-rounded-corner with-shadow pb-2">
                  <div className="panel-heading">
                    <span className="panel-title">Activity History</span>
                  </div>
                  <div id="activityTarget">
                    <div
                      className="commentbox"
                      id="activityData"
                      style={{
                        maxHeight: "400px", // adjust height as needed
                        overflowY: "auto",
                        paddingRight: "10px", // optional, for scrollbar spacing
                      }}
                    >
                      {loading || isRendering ? (
                        <div className="p-2">
                          {[...Array(5)].map((_, i) => (
                            <div key={i} className="mb-3">
                              <div
                                className="skeleton-text"
                                style={{
                                  width: "100%",
                                  height: "14px",
                                  marginBottom: "5px",
                                }}
                              ></div>
                              <div
                                className="skeleton-text"
                                style={{ width: "70%", height: "12px" }}
                              ></div>
                              <hr />
                            </div>
                          ))}
                        </div>
                      ) : activityList && activityList.length > 0 ? (
                        activityList.map((activity, index) => (
                          <p key={index}>
                            <span style={{ color: "slategray", fontWeight: "bold" }}>
                              {activity.workbyname} Updated {activity.colname}
                            </span>{" "}
                            <br />
                            {activity.message}
                            <span style={{ color: "blue", fontWeight: "bold" }}>
                              {" "}
                              on{" "}
                              {activity.created_on
                                ? moment(activity.created_on).format("DD-MM-YYYY hh:mm A")
                                : ""}
                            </span>
                          </p>
                        ))
                      ) : (
                        <p className="ps-3">No Activities Done</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      {isModalSubscriberOpen && (
        <div
          className="modal-backdrop fade show"
          style={{
            backgroundColor: "rgba(0, 0, 0, 1)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1040,
          }}
        ></div>
      )}

      {isModalSubscriberOpen && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog" style={{ maxWidth: "30rem" }}>
            <div class="modal-content">
              <div class="modal-header">
                <div class="col-md-6">
                  <h5 class="modal-title" style={{ fontWeight: "bold" }}>
                    Update Subscriber
                  </h5>
                </div>
                <div class="col-md-6 text-end">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={closeModal}
                  >
                    &times;
                  </button>
                </div>
                <div class="clearfix"></div>
              </div>
              <div class="modal-body">
                <div class="panel-body">
                  <div class="row">
                    <table
                      class="table table-condensed table-hover bg-white usertable"
                      width="100%"
                    >
                      <tbody>
                        <tr class="subscriber-block">
                          <td>
                            <label>Subscriber</label>

                            <AsyncSelect
                              cacheOptions
                              defaultOptions={false}
                              loadOptions={loadOptions}
                              inputValue={subscriberSearch}
                              onInputChange={(val) => setSubscriberSearch(val)}
                              value={
                                subscriberOptions.find(
                                  (opt) => opt.value == formValues.subscriber
                                ) || null
                              }
                              onChange={handleAsyncSelectChange}
                              isMulti={false} // equivalent to multiple: false
                              placeholder="Select Subscriber..."
                              noOptionsMessage={() =>
                                subscriberSearch.length < 3
                                  ? "Please enter 3 or more characters"
                                  : "No subscribers found"
                              }
                              loadingMessage={() => "Searching subscribers..."}
                              openMenuOnClick={true}
                              openMenuOnFocus={true}
                              menuPlacement="auto"
                              name="subscriber" // 👈 important for generic handling
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div
                      class="d-flex gap-2 justify-content-end"
                      style={{ textAlign: "right" }}
                    >
                      <button
                        type="button"
                        name="submit"
                        class="btn btn-primary"
                        onClick={() => handleSave("subscriber", formValues.subscriber)}
                      >
                        Submit
                      </button>
                      <button
                        type="button"
                        name="cancel"
                        class="btn btn-default"
                        onClick={closeModal}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Parent Order Modal */}
      <AssignParentOrderPopup
        orderId={id}
        isOpen={isAssignParentOrderModalOpen}
        onClose={closeAssignParentOrderModal}
        onSuccess={handleAssignParentOrderSuccess}
      />
    </div>
  );
};

export default OrderEdit;
